/**
 * The "+ Add item" row: the last row of every group, above the footer
 * (spec M1-B3 §8.2, M1-B4 §8.10). Clicking it starts a draft at the **bottom** of that group;
 * `Enter` commits and immediately opens a new draft below it; `Escape` or an empty name
 * discards the draft and creates nothing (ASSUMED, Q152).
 *
 * **P-291 — a multi-line paste creates one item per line.** Measured on the sandbox board
 * (`discovery/behavior-notes/P-291-paste-creates-items.md`): three lines pasted into monday's own
 * `+ Add item` input created three items, named line by line, in that group, in that order, with
 * **no cells filled, no toast, and the input left empty and still open**. That is the gesture
 * Gabriela's "paste a list of rows from a spreadsheet" means, and ours used to drop every line
 * but the first into the text box. A single-line paste is left to the browser: it is a name the
 * user may still edit before `Enter`.
 */
import type { GroupId } from '@elizmonday/shared';
import { useState } from 'react';
import { CHECKBOX_GUTTER, GROUP_BAR_WIDTH } from './layout';

export interface AddItemRowProps {
  groupId: GroupId;
  groupColor: string;
  top: number;
  height: number;
  nameWidth: number;
  stickyWidth: number;
  scrollWidth: number;
  terminology: string;
  /** A dragged row is hovering: this is the only way to reach the bottom of a group. */
  dropEdge?: boolean;
  onAdd(name: string): void | Promise<void>;
  /** P-291: one item per pasted line. Absent = the row cannot take a multi-line paste. */
  onAddMany?(names: readonly string[]): void | Promise<void>;
}

export function AddItemRow({
  groupId,
  groupColor,
  top,
  height,
  nameWidth,
  stickyWidth,
  scrollWidth,
  terminology,
  dropEdge = false,
  onAdd,
  onAddMany,
}: AddItemRowProps) {
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState('');

  /** The names a pasted block carries: one per non-empty line, in order. */
  const namesOf = (text: string): string[] =>
    text
      .split(/\r\n|\r|\n/)
      .map((line) => line.trim())
      .filter((line) => line !== '');

  const commit = async (keepGoing: boolean) => {
    const name = draft.trim();
    setDraft('');
    if (name === '') {
      setDrafting(false);
      return;
    }
    await onAdd(name);
    setDrafting(keepGoing);
  };

  return (
    <div
      className="ez-add-item"
      role="row"
      data-testid={`add-item-${groupId}`}
      // Dropping here appends to the group (`Grid` delegates the drag events).
      data-drop-row="group-end"
      data-group={groupId}
      data-drop-edge={dropEdge ? 'above' : undefined}
      style={{ top, height, width: stickyWidth + scrollWidth }}
    >
      <div className="ez-add-item__sticky" role="presentation" style={{ width: stickyWidth }}>
        <span
          className="ez-row__bar"
          style={{ width: GROUP_BAR_WIDTH, backgroundColor: groupColor }}
          aria-hidden="true"
        />
        <span
          className="ez-row__gutter"
          role="gridcell"
          style={{ width: CHECKBOX_GUTTER }}
          aria-hidden="true"
        />
        <div className="ez-add-item__name" role="gridcell" style={{ width: nameWidth }}>
          {drafting ? (
            <input
              className="ez-cell__input"
              data-testid={`add-item-input-${groupId}`}
              autoFocus
              value={draft}
              aria-label={`New ${terminology} name`}
              placeholder={`+ Add ${terminology}`}
              onChange={(event) => setDraft(event.target.value)}
              /*
               * P-291. Only a block of **two or more** lines is taken over: one line is an
               * ordinary paste into the text box, which is what monday does and what lets a
               * user paste a name and then edit it.
               */
              onPaste={(event) => {
                /*
                 * **P-302 — the single line is stopped here too.**
                 *
                 * The grid's scroller is this input's ancestor and owns the board's clipboard
                 * surface; a paste that fell through to the browser reached it and was
                 * cancelled there, so pasting a name into the open `+ Add item` input did
                 * nothing at all. `Grid` no longer takes an editable element's paste, and the
                 * row stops the event regardless: whichever of the two ships first, the name
                 * lands in the box.
                 */
                event.stopPropagation();
                if (!onAddMany) return;
                const names = namesOf(event.clipboardData.getData('text/plain'));
                // One line is a draft the user may still edit: the browser inserts it (P-291).
                if (names.length < 2) return;
                event.preventDefault();
                setDraft('');
                // The input stays open and empty, as monday's does after the rows appear.
                void onAddMany(names);
              }}
              onKeyDown={(event) => {
                event.stopPropagation();
                if (event.key === 'Enter') void commit(true);
                if (event.key === 'Escape') {
                  setDraft('');
                  setDrafting(false);
                }
              }}
              onBlur={() => void commit(false)}
            />
          ) : (
            <button
              type="button"
              className="ez-add-item__button"
              data-testid={`add-item-submit-${groupId}`}
              onClick={() => setDrafting(true)}
            >
              {`+ Add ${terminology}`}
            </button>
          )}
        </div>
      </div>
      <div
        className="ez-add-item__scroll"
        role="presentation"
        style={{ width: scrollWidth }}
        aria-hidden="true"
      />
    </div>
  );
}
